import { memo, useCallback } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useShop } from '@/contexts';
import PixelIcon from '@/components/ui/PixelIcon';
import type { GameState } from '@/types/game';
import type { CardInstance } from '@/types/card';
import { CARD_DEFINITIONS, RARITY_COLORS } from '@/config/cards';
import { getWorldForLevel } from '@/config/worlds';

interface HUD3DProps {
  gameState: GameState;
  isExpanded: boolean;
  onToggle: () => void;
  onOpenSettings: () => void;
  onRestart: () => void;
  onQuit: () => void;
  onPlayCard?: (cardId: string) => void;
  hand?: CardInstance[];
}

// Shared glass panel style
const glassPanel: React.CSSProperties = {
  background: 'rgba(10, 10, 30, 0.65)',
  backdropFilter: 'blur(12px)',
  WebkitBackdropFilter: 'blur(12px)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  borderRadius: 14,
};

// Compact stat chip
function StatChip({ icon, value, color, label }: {
  icon: React.ReactNode;
  value: string | number;
  color: string;
  label?: string;
}) {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 5,
      padding: '5px 10px',
      ...glassPanel,
      borderRadius: 10,
      borderColor: `${color}22`,
    }}>
      {icon}
      <span style={{
        color: '#fff',
        fontSize: 13,
        fontWeight: 700,
        fontFamily: 'system-ui, sans-serif',
        minWidth: 16,
        textAlign: 'right',
        textShadow: '0 1px 3px rgba(0,0,0,0.5)',
      }}>
        {typeof value === 'number' && value >= 10000
          ? `${(value / 1000).toFixed(1)}k`
          : value}
      </span>
      {label && (
        <span style={{
          color: 'rgba(255,255,255,0.5)',
          fontSize: 10,
          fontFamily: 'system-ui, sans-serif',
        }}>
          {label}
        </span>
      )}
    </div>
  );
}

function HUD3DInner({
  gameState,
  isExpanded,
  onToggle,
  onOpenSettings,
  onRestart,
  onQuit,
  onPlayCard,
  hand = [],
}: HUD3DProps) {
  const { shopState } = useShop();
  const world = getWorldForLevel(gameState.level);
  const accentColor = world.id === 'emberdeep' ? '#FF7043'
    : world.id === 'frostline' ? '#4DD0E1'
    : world.id === 'arcana' ? '#AB47BC'
    : world.id === 'stonehollow' ? '#90A4AE'
    : '#4ecdc4';

  const levelLabel = gameState.gameMode === 'endless'
    ? `W${gameState.infiniteState?.wave || 1}`
    : `Lv ${gameState.level + 1}`;

  return (
    <>
      {/* ---- TOP BAR ---- */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2, duration: 0.4 }}
        style={{
          position: 'fixed',
          top: 'max(8px, env(safe-area-inset-top, 0px))',
          left: 8,
          right: 8,
          zIndex: 5000,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          pointerEvents: 'none',
        }}
      >
        {/* Left: Level + Lives */}
        <div style={{ display: 'flex', gap: 6, pointerEvents: 'auto' }}>
          <motion.button
            onClick={onToggle}
            whileTap={{ scale: 0.92 }}
            style={{
              ...glassPanel,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '6px 14px',
              cursor: 'pointer',
              borderColor: `${accentColor}33`,
            }}
          >
            <span style={{
              color: accentColor,
              fontSize: 12,
              fontWeight: 800,
              fontFamily: '"Press Start 2P", monospace',
              textShadow: `0 0 8px ${accentColor}44`,
            }}>
              {levelLabel}
            </span>
            <div style={{
              width: 1,
              height: 16,
              background: 'rgba(255,255,255,0.15)',
            }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <PixelIcon name="life" size={14} color="#ff6b6b" />
              <span style={{
                color: '#fff',
                fontSize: 13,
                fontWeight: 700,
                fontFamily: 'system-ui, sans-serif',
              }}>
                {shopState.lives}
              </span>
            </div>
          </motion.button>
        </div>

        {/* Center: Stats */}
        <div style={{ display: 'flex', gap: 5, pointerEvents: 'auto' }}>
          <StatChip
            icon={<PixelIcon name="score" size={13} color="#FFD700" />}
            value={gameState.gameStats.score}
            color="#FFD700"
          />
          <StatChip
            icon={<PixelIcon name="coin" size={13} color="#FFD700" />}
            value={shopState.coins}
            color="#FFD700"
          />
          <StatChip
            icon={<PixelIcon name="gem" size={13} color="#AB47BC" />}
            value={shopState.gems}
            color="#AB47BC"
          />
        </div>

        {/* Right: Settings */}
        <div style={{ display: 'flex', gap: 6, pointerEvents: 'auto' }}>
          <motion.button
            onClick={onOpenSettings}
            whileTap={{ scale: 0.9 }}
            style={{
              ...glassPanel,
              padding: '7px 10px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <PixelIcon name="settings" size={16} color="rgba(255,255,255,0.7)" />
          </motion.button>
        </div>
      </motion.div>

      {/* ---- BOTTOM LEFT: Steps counter ---- */}
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.4 }}
        style={{
          position: 'fixed',
          bottom: 'max(12px, env(safe-area-inset-bottom, 0px))',
          left: 12,
          zIndex: 5000,
          pointerEvents: 'none',
        }}
      >
        <StatChip
          icon={<PixelIcon name="play" size={12} color={accentColor} />}
          value={gameState.gameStats.stepsTaken}
          color={accentColor}
          label="steps"
        />
      </motion.div>

      {/* ---- EXPANDED OVERLAY ---- */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 5500,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(0, 0, 0, 0.7)',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
            }}
            onClick={onToggle}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 400, damping: 30 }}
              onClick={(e) => e.stopPropagation()}
              style={{
                ...glassPanel,
                background: 'rgba(10, 10, 35, 0.85)',
                padding: 24,
                width: '90%',
                maxWidth: 360,
                display: 'flex',
                flexDirection: 'column',
                gap: 16,
              }}
            >
              {/* Title */}
              <div style={{
                textAlign: 'center',
                color: '#fff',
                fontSize: 14,
                fontWeight: 800,
                fontFamily: '"Press Start 2P", monospace',
                letterSpacing: '0.5px',
              }}>
                {levelLabel}
              </div>

              {/* Stats grid */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 8,
              }}>
                {[
                  { icon: 'score' as const, label: 'Score', value: gameState.gameStats.score, color: '#FFD700' },
                  { icon: 'play' as const, label: 'Steps', value: gameState.gameStats.stepsTaken, color: accentColor },
                  { icon: 'coin' as const, label: 'Coins', value: shopState.coins, color: '#FFD700' },
                  { icon: 'gem' as const, label: 'Gems', value: shopState.gems, color: '#AB47BC' },
                ].map((stat) => (
                  <div
                    key={stat.label}
                    style={{
                      ...glassPanel,
                      borderRadius: 10,
                      padding: '10px 12px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      borderColor: `${stat.color}22`,
                    }}
                  >
                    <PixelIcon name={stat.icon} size={16} color={stat.color} />
                    <div>
                      <div style={{
                        color: '#fff',
                        fontSize: 15,
                        fontWeight: 700,
                        fontFamily: 'system-ui, sans-serif',
                      }}>
                        {stat.value}
                      </div>
                      <div style={{
                        color: 'rgba(255,255,255,0.45)',
                        fontSize: 10,
                        fontFamily: 'system-ui, sans-serif',
                      }}>
                        {stat.label}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Cards in hand */}
              {hand.length > 0 && (
                <div>
                  <div style={{
                    color: 'rgba(255,255,255,0.5)',
                    fontSize: 10,
                    fontFamily: 'system-ui, sans-serif',
                    marginBottom: 6,
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                  }}>
                    Cards
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {hand.map((card) => {
                      const info = CARD_DEFINITIONS[card.type];
                      const borderColor = RARITY_COLORS[card.rarity];
                      return (
                        <motion.button
                          key={card.id}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => onPlayCard?.(card.id)}
                          style={{
                            ...glassPanel,
                            padding: '8px 12px',
                            borderColor,
                            borderRadius: 8,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                          }}
                        >
                          <PixelIcon name={info.icon} size={14} color={borderColor} />
                          <span style={{
                            color: '#fff',
                            fontSize: 11,
                            fontFamily: 'system-ui, sans-serif',
                            fontWeight: 600,
                          }}>
                            {info.name}
                          </span>
                        </motion.button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Action buttons */}
              <div style={{ display: 'flex', gap: 8 }}>
                <ActionButton
                  label="Resume"
                  color={accentColor}
                  onClick={onToggle}
                  flex={2}
                />
                <ActionButton
                  label="Restart"
                  color="#f39c12"
                  icon="restart"
                  onClick={() => { onToggle(); onRestart(); }}
                  flex={1}
                />
                <ActionButton
                  label="Quit"
                  color="#e74c3c"
                  icon="home"
                  onClick={() => { onToggle(); onQuit(); }}
                  flex={1}
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function ActionButton({ label, color, icon, onClick, flex = 1 }: {
  label: string; color: string; icon?: string; onClick: () => void; flex?: number;
}) {
  return (
    <motion.button
      whileTap={{ scale: 0.94 }}
      onClick={onClick}
      style={{
        flex,
        padding: '10px 8px',
        borderRadius: 10,
        border: `1px solid ${color}55`,
        background: `${color}22`,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 5,
      }}
    >
      {icon && <PixelIcon name={icon as any} size={12} color={color} />}
      <span style={{
        color,
        fontSize: 11,
        fontWeight: 700,
        fontFamily: 'system-ui, sans-serif',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
      }}>
        {label}
      </span>
    </motion.button>
  );
}

const HUD3D = memo(HUD3DInner);
export default HUD3D;
